/**
 * Study Anything: upload any book as Markdown (up to 250 pages) and chat with it.
 *
 * The library page: the user's books, an upload box, and a way to remove a
 * book. Opening a book goes to the reader (./StudyReader), where the AI tutor
 * answers from the book by searching it — no preparation, no indexing: the
 * first question is answerable the moment the upload finishes.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Card, CardContent, CircularProgress, Container, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";

import NavbarLoggedInNew from "components/Navbar-new/NavbarLoggedInNew";
import { ConfirmDialog } from "../ContentBank/dialogs";
import { deleteBook, errorMessage, listBooks, uploadBook } from "./studyApi";

const FONT = "Be Vietnam Pro";
export const MAX_PAGES = 250;
const ACCEPT = ".md,.markdown,.txt";

const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

export default function StudyLibrary() {
  const navigate = useNavigate();
  const [books, setBooks] = useState(null);
  const [error, setError] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(null);     // a book row
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setBooks(await listBooks());
    } catch (e) {
      setError(errorMessage(e, "Couldn't load your books"));
      setBooks([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file || uploading) return;
    setUploading(true);
    setError("");
    try {
      const row = await uploadBook(file, title);
      setBooks((cur) => [row, ...(cur || [])]);
      setFile(null);
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      navigate(`/dashboard/study/${row.id}`);
    } catch (e) {
      setError(errorMessage(e, "Couldn't upload that file"));
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await deleteBook(removing.id);
      setBooks((cur) => (cur || []).filter((b) => b.id !== removing.id));
      setRemoving(null);
    } catch (e) {
      setError(errorMessage(e, "Couldn't remove that book"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <NavbarLoggedInNew />
      <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <AutoStoriesOutlinedIcon sx={{ color: "#0172A6", fontSize: 30 }} />
          <Typography component="h1" sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 600, color: "#202124" }}>Study Anything</Typography>
        </Box>
        <Typography sx={{ fontFamily: FONT, color: "#6b7280", fontSize: 14, mb: 3, maxWidth: 640 }}>
          Upload any book as a Markdown file, up to {MAX_PAGES} pages, and read it with the AI tutor beside you.
          The tutor searches the book for every question, the way grep searches a codebase — nothing to prepare, nothing to index.
          Only you can see your books and your conversations.
        </Typography>

        <Card variant="outlined" sx={{ mb: 4, borderRadius: "12px" }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: 15, mb: 1.5 }}>Add a book</Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
              <input ref={fileRef} type="file" accept={ACCEPT} hidden data-testid="study-file"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <Button variant="outlined" startIcon={<UploadFileOutlinedIcon />} onClick={() => fileRef.current?.click()}
                sx={{ textTransform: "none", fontFamily: FONT, borderRadius: "8px" }}>
                {file ? file.name : "Choose a .md file"}
              </Button>
              <TextField size="small" label="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)}
                inputProps={{ maxLength: 200, "aria-label": "Book title" }} sx={{ minWidth: 240, flex: 1 }} />
              <Button variant="contained" onClick={upload} disabled={!file || uploading}
                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ textTransform: "none", fontFamily: FONT, borderRadius: "8px" }}>
                {uploading ? "Uploading…" : "Upload and open"}
              </Button>
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#6b7280", mt: 1 }}>
              Markdown (.md), UTF-8, under {MAX_PAGES} pages (about 3,000 characters a page). The first heading becomes the title unless you give one.
            </Typography>
          </CardContent>
        </Card>

        {error && <Typography role="alert" sx={{ fontFamily: FONT, fontSize: 13, color: "#b42318", mb: 2 }}>{error}</Typography>}

        {books === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : books.length === 0 ? (
          <Typography sx={{ fontFamily: FONT, color: "#6b7280", fontSize: 14, textAlign: "center", py: 4 }}>
            No books yet. Upload one above to start.
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            {books.map((b) => (
              <Card key={b.id} variant="outlined" data-testid="study-book-card"
                sx={{ borderRadius: "12px", cursor: "pointer", "&:hover": { borderColor: "#0172A6", boxShadow: "0 2px 8px rgba(1,114,166,0.12)" } }}
                onClick={() => navigate(`/dashboard/study/${b.id}`)}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 }, display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                  <AutoStoriesOutlinedIcon sx={{ color: "#0172A6", mt: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: 15, color: "#202124", overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.title}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: "#6b7280" }}>
                      {b.pages} page{b.pages === 1 ? "" : "s"}{b.uploaded_at ? ` · added ${when(b.uploaded_at)}` : ""}
                    </Typography>
                  </Box>
                  <Tooltip title="Remove this book">
                    <IconButton size="small" aria-label={`Remove ${b.title}`}
                      onClick={(e) => { e.stopPropagation(); setRemoving(b); }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Container>

      <ConfirmDialog open={!!removing} title="Remove this book?" confirmLabel="Remove" busy={busy}
        body={removing ? `"${removing.title}" and your conversations about it are removed from your library.` : ""}
        onConfirm={remove} onClose={() => setRemoving(null)} />
    </>
  );
}
